import React, { Component } from 'react';
import { Button, Modal, FormControl, FormGroup, ControlLabel, Checkbox } from 'react-bootstrap';
import { connect } from 'react-redux';

import OverlayLoader from '../components/overlayLoader';
import { showModalMessage } from '../store/modalsSlice';

import * as trialRejectedDevicesActions from '../store/trialRejectedDevicesSlice';
import { trialRejectedDevicesSelectors } from '../store/trialRejectedDevicesSlice';
import * as devicesActions from '../store/devicesSlice';
import { devicesSelectors } from '../store/devicesSlice';
import { unwrapResult } from '@reduxjs/toolkit';
import { TrialRejectedDevicesTableColumnCaptions } from '../components/tableColumnCaptions';
import DeviceKeyInputNormalized from '../components/deviceKeyInputNormalized';
import { isDeviceKeyValid } from '../common/keyValidation';

const Mode = {
  Create: 0,
  Edit: 1,
};

const Titles = {
  [Mode.Create]: "Create new trial rejected device",
  [Mode.Edit]: "Edit trial rejected device",
};

const SubmitButtonCaptions = {
  [Mode.Create]: "Create",
  [Mode.Edit]: "Save",
};

class TrialRejectedDevicesEditDIalog extends Component {
  initState = {
    deviceKey: '',
    isOn: false,
    loadedDevice: null,
  };

  constructor(props) {
    super(props);
    this.handleClose = this.props.onClose;

    this.state = this.initState;

    this.onShow = this.onShow.bind(this);
    this.onClose = this.onClose.bind(this);
    this.onIsOnChange = this.onIsOnChange.bind(this);
    this.onDeviceKeyChange = this.onDeviceKeyChange.bind(this);

    this.onSubmit = this.onSubmit.bind(this);
  }

  static get Mode() {
    return Mode;
  }

  onShow() {
    this.setState(this.initState);
    if (this.props.mode === Mode.Edit) {
      this.setState({ isOn: this.props.isOn });
    }
  }

  onClose() {
    this.handleClose();
    this.setState(this.initState);
  }

  onIsOnChange(checked) {
    this.setState({ isOn: checked });
  }

  onDeviceKeyChange(normalizedDeviceKey) {
    this.setState({ deviceKey: normalizedDeviceKey }, this.loadDeviceInfo);
  }

  loadDeviceInfo() {
    if (this.isCorrectDeviceKey()) {
      this.props.getDeviceByKey({ deviceKey: this.state.deviceKey.trim() })
        .then(unwrapResult)
        .then((device) => {
          this.setState({ loadedDevice: device });
        })
    }
    else {
      this.setState({ loadedDevice: null });
    }
  }

  isCorrectDeviceKey() {
    return isDeviceKeyValid(this.state.deviceKey.trim());
  }

  isLoading() {
    if (this.props.mode === Mode.Create)
      return this.props.isTrialRejectedDeviceCreating || this.props.isDeviceLoading;
    else if (this.props.mode === Mode.Edit)
      return this.props.isTrialRejectedDeviceUpdating;
  }

  getLoaderMessage() {
    if (this.props.isTrialRejectedDeviceCreating) {
      return "Trial rejected device creating...";
    }
    if (this.props.isTrialRejectedDeviceUpdating) {
      return "Trial rejected device updating...";
    }
    if (this.props.isDeviceLoading) {
      return "Device loading...";
    }
  }

  isSubmitButtonDisabled() {
    if (this.props.mode === Mode.Edit) {
      return this.isLoading() || (this.state.isOn === this.props.isOn);
    }
    return this.isLoading() ||
      !this.isCorrectDeviceKey() ||
      this.isDeviceAlreadyWasRejected(this.state.deviceKey, this.props.trialRejectedDevices) ||
      (this.props.mode === Mode.Create && this.state.loadedDevice === null);
  }

  onSubmit() {
    this.onClose();
    let submitPromise;

    const { loadedDevice, isOn } = this.state;

    switch (this.props.mode) {
      case Mode.Create:
        submitPromise = this.props.addTrialRejectedDevice({ deviceId: loadedDevice.id })
        break;
      case Mode.Edit:
        submitPromise = this.props.editTrialRejectedDevice({ id: this.props.id, isOn })
        break;
      default:
        submitPromise = null;
    }

    if (submitPromise == null) {
      this.props.showModalMessage('Error', 'Unknown dialog mode');
      return;
    }

    return submitPromise
      .then(unwrapResult)
      .catch((e) => {
        console.log(e);
        console.log(this.props.onSubmitError);
        this.props.showModalMessage("Error", this.props.onSubmitError.toString());
      });
  }

  renderStatic() {
    return (
      <FormGroup controlId="fcDeviceKey">
        <ControlLabel>{TrialRejectedDevicesTableColumnCaptions.device.key}</ControlLabel>
        <DeviceKeyInputNormalized onChange={this.onDeviceKeyChange} />
      </FormGroup>
    );
  }

  renderDynamic() {
    return this.isCorrectDeviceKey() ?
      this.renderLoadingResults() :
      this.renderDeviceKeyError();
  }

  renderLoadingResults() {
    if (this.state.loadedDevice === null) {
      if (this.props.isDeviceLoading)
        return null;
      return <FormControl.Static className="text-danger">Device is not found</FormControl.Static>;
    }
    let device = this.state.loadedDevice;
    if (device && this.isDeviceAlreadyWasRejected(device.key, this.props.trialRejectedDevices)) {
      return (
        <FormControl.Static className="text-danger">
          This device was already rejected
        </FormControl.Static>
      );
    }
    return <FormControl.Static className="text-success">Device has been found</FormControl.Static>;
  }

  isDeviceAlreadyWasRejected(deviceKey, rejectedDevices) {
    return rejectedDevices.find((rejectedDevice) => deviceKey === rejectedDevice.device.key);
  }

  renderDeviceKeyError() {
    let message = this.state.deviceKey.length === 0 ? {
      text: "Enter device key",
      className: "text-info"
    } : {
      text: "Device key is not correct",
      className: "text-danger"
    };
    return <FormControl.Static className={message.className}>{message.text}</FormControl.Static>;
  }

  render() {
    const isEditMode = this.props.mode === Mode.Edit;

    return (
      <Modal bsSize="small" show={this.props.show} onShow={this.onShow} onHide={this.onClose} backdrop="static">
        <Modal.Header>
          <Modal.Title>{Titles[this.props.mode]}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          <OverlayLoader active={this.isLoading()} text={this.getLoaderMessage()}>
            {isEditMode ? 
              <FormGroup controlId="fcIsOn" disabled>
                <Checkbox
                  onChange={e => this.onIsOnChange(e.target.checked)}
                  checked={this.state.isOn}
                >
                  {TrialRejectedDevicesTableColumnCaptions.isOn}
                </Checkbox>
              </FormGroup> :
              <React.Fragment>
                {this.renderStatic()}
                {this.renderDynamic()}
              </React.Fragment>
            }
          </OverlayLoader>
        </Modal.Body>
        <Modal.Footer>
          <Button onClick={this.onSubmit} disabled={this.isSubmitButtonDisabled()}>
            {SubmitButtonCaptions[this.props.mode]}
          </Button>
          <Button onClick={this.onClose}>Close</Button>
        </Modal.Footer>
      </Modal>
    );
  }
}

const mapStateToProps = (state) => ({
  trialRejectedDevices: trialRejectedDevicesSelectors.selectAll(state),

  isTrialRejectedDeviceCreating: trialRejectedDevicesSelectors.isCreating(state),
  isTrialRejectedDeviceUpdating: trialRejectedDevicesSelectors.isUpdating(state),
  isDeviceLoading: devicesSelectors.isFetching(state),
  deviceLoadingError: devicesSelectors.fetchingError(state),

  onSubmitError: trialRejectedDevicesSelectors.creatingUpdatingError(state),
});

const actionCreators = {
  addTrialRejectedDevice: trialRejectedDevicesActions.create,
  editTrialRejectedDevice: trialRejectedDevicesActions.update,
  getDeviceByKey: devicesActions.fetchByKey,

  showModalMessage: (title, text) => showModalMessage({ title, text }),
};


export default connect(mapStateToProps, actionCreators)(TrialRejectedDevicesEditDIalog);


// WEBPACK FOOTER //
// ./src/containers/trialRejectedDeviceEditDIalog.js